/**
 * PublicBailVerifyPage — Page publique de vérification d'un bail signé.
 *
 * Accessible via QR code imprimé sur le PDF signé. Aucune auth requise.
 * Affiche : titre, statut, liste des signataires avec timestamps,
 * + bannière de validation conforme TAL.
 *
 * Batch #14.162.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, Shield, User, Building2, Loader2 } from "lucide-react";
import { API } from "@/api";


const ROLE_LABEL = {
  tenant: { label: "Locataire", icon: User, color: "text-blue-600 bg-blue-50 border-blue-200" },
  owner: { label: "Propriétaire", icon: Building2, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
};

export default function PublicBailVerifyPage() {
  const { workflowId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Désactive Google Translate sur cette page publique (cf. Batch #14.160 — évite NotFoundError React)
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "google";
    meta.content = "notranslate";
    document.head.appendChild(meta);
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
    return () => {
      try { document.head.removeChild(meta); } catch { /* noop */ }
      document.documentElement.removeAttribute("translate");
      document.documentElement.classList.remove("notranslate");
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/bail-verify/${workflowId}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "Ce bail n'existe pas." : "Erreur de chargement.");
      } finally {
        setLoading(false);
      }
    })();
  }, [workflowId]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" });
    } catch {
      return String(iso);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center notranslate" translate="no">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 notranslate" translate="no" data-testid="bail-verify-error">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border border-rose-200">
          <XCircle className="w-16 h-16 mx-auto text-rose-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Vérification impossible</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">DG Comptabilité — Service de vérification de baux TAL</p>
        </div>
      </div>
    );
  }

  const isComplete = data?.is_complete;
  const isDemo = data?.is_demo;
  const signedCount = (data?.signers || []).filter((s) => s.signed).length;
  const totalCount = (data?.signers || []).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 notranslate"
         translate="no" data-testid="bail-verify-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* Bannière statut */}
        <div className={`rounded-2xl shadow-lg p-6 mb-6 border-2 ${
          isComplete
            ? "bg-emerald-600 border-emerald-700 text-white"
            : "bg-amber-500 border-amber-600 text-white"
        }`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              {isComplete ? <CheckCircle2 className="w-8 h-8" /> : <Clock className="w-8 h-8" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold">
                {isComplete ? "Bail valide" : "Signatures en cours"}
              </h1>
              <p className="text-sm opacity-95 mt-0.5">
                {isComplete
                  ? "Signature électronique conforme TAL"
                  : `${signedCount}/${totalCount} signature(s) enregistrée(s)`}
              </p>
            </div>
            {isDemo && (
              <span className="px-3 py-1 rounded-full bg-rose-700 text-white text-xs font-bold">
                DÉMO
              </span>
            )}
          </div>
        </div>

        {/* Détails */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 border border-slate-200">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Document</h2>
          <p className="text-base font-semibold text-slate-800 mb-1" data-testid="bail-verify-title">
            {data?.title || "Bail TAL"}
          </p>
          <p className="text-xs text-slate-500 font-mono">
            ID : {(data?.workflow_id || "").slice(0, 8)}…{(data?.workflow_id || "").slice(-4)}
          </p>
          {isComplete && data?.completed_at && (
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-500 block">Bail finalisé le</span>
                <span className="font-semibold text-slate-700">{fmtDate(data.completed_at)}</span>
              </div>
              {data?.finalized_at && (
                <div>
                  <span className="text-slate-500 block">PDF généré le</span>
                  <span className="font-semibold text-slate-700">{fmtDate(data.finalized_at)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Liste des signataires */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 border border-slate-200">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Signataires ({signedCount}/{totalCount})
          </h2>
          <div className="space-y-2" data-testid="bail-verify-signers">
            {(data?.signers || []).map((s, i) => {
              const r = ROLE_LABEL[s.role] || ROLE_LABEL.tenant;
              const Icon = r.icon;
              return (
                <div key={i}
                     className={`flex items-center gap-3 p-3 rounded-xl border ${r.color}`}
                     data-testid={`bail-verify-signer-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{s.name || "—"}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">{r.label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {s.signed ? (
                      <>
                        <div className="flex items-center justify-end gap-1 text-emerald-600">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-xs font-semibold">Signé</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{fmtDate(s.signed_at)}</p>
                      </>
                    ) : (
                      <div className="flex items-center justify-end gap-1 text-amber-600">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-semibold">En attente</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer info */}
        <div className="bg-slate-100 rounded-xl p-4 text-center">
          <Shield className="w-6 h-6 mx-auto text-slate-400 mb-1.5" />
          <p className="text-xs text-slate-600">
            Cette page de vérification confirme l'authenticité du document signé électroniquement.
          </p>
          <p className="text-[10px] text-slate-400 mt-1">
            DG Comptabilité — Service de signature de bail TAL conforme aux normes du Tribunal administratif du logement (Québec)
          </p>
        </div>
      </div>
    </div>
  );
}
